import { Boundaries } from "@planzone/core";
import { events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  zone: Boundaries["retrieveZoneProjectsReports"]["request"],
): State {
  const index = state.findIndex((req) => req.zone.uuid === zone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { zone, request: setRequestStateToPending(), reports: [] },
        ...state.slice(index + 1),
      ]
    : [...state, { zone, request: setRequestStateToPending(), reports: [] }];
}

function handleSuccess(
  state: State,
  { reports, zone }: Boundaries["retrieveZoneProjectsReports"]["success"],
): State {
  const index = state.findIndex((req) => req.zone.uuid === zone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { zone, request: setRequestStateToSuccess(), reports },
        ...state.slice(index + 1),
      ]
    : [...state, { zone, request: setRequestStateToSuccess(), reports }];
}

function handleFailure(
  state: State,
  zone: Boundaries["retrieveZoneProjectsReports"]["request"],
): State {
  const index = state.findIndex((req) => req.zone.uuid === zone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [...state, { zone, request: setRequestStateToFailure(), reports: [] }];
}

function handleRemoveReport(
  state: State,
  { report }: Boundaries["removeProjectReport"]["success"],
): State {
  return state.map((res) => ({
    ...res,
    reports: res.reports.filter(({ report: r }) => r.uuid !== report.uuid),
  }));
}

function handleEditReport(
  state: State,
  { report }: Boundaries["editProjectReport"]["success"],
): State {
  return state.map((res) => ({
    ...res,
    reports: res.reports.filter(({ report: r }) =>
      r.uuid === report.uuid ? report : r,
    ),
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveZoneProjectsReportsRequested]: handleRequest,
    [events.retrieveZoneProjectsReportsSucceeded]: handleSuccess,
    [events.retrieveZoneProjectsReportsFailed]: handleFailure,
    [events.removeProjectReportSucceeded]: handleRemoveReport,
    [events.editProjectReportSucceeded]: handleEditReport,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
